import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { logout } from '../../store/authSlice';
import { authService } from '../../services/authService';
import { Button } from '../UI/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../UI/dialog';

const EVENTS = ['mousemove', 'keydown', 'wheel', 'mousedown', 'touchstart'];

const SessionTimeout = ({
    timeoutDuration = 30 * 60 * 1000, // Default 30 minutes
    warningDuration = 60 * 1000       // Default 1 minute warning
}) => {
    const dispatch = useDispatch();
    const [showWarning, setShowWarning] = useState(false);
    const [timeLeft, setTimeLeft] = useState(warningDuration / 1000);

    const timerRef = useRef(null);
    const warningTimerRef = useRef(null);
    const lastActivityRef = useRef(Date.now());

    // Function to handle logout
    const handleLogout = useCallback(async () => {
        try {
            await authService.logout();
        } catch (error) {
            console.error('Logout failed:', error);
        } finally {
            dispatch(logout());
            // Also force a window reload to clear any robust state
            window.location.href = '/login';
        }
    }, [dispatch]);

    // Timer Logic
    const startTimer = useCallback(() => {
        if (timerRef.current) clearTimeout(timerRef.current);
        if (warningTimerRef.current) clearInterval(warningTimerRef.current);

        // Set timer for when the warning should appear
        const warningTime = timeoutDuration - warningDuration;

        timerRef.current = setTimeout(() => {
            // Trigger SMS warning
            authService.sendSessionWarning();

            setShowWarning(true);
            setTimeLeft(warningDuration / 1000);

            // Start countdown for the warning dialog
            warningTimerRef.current = setInterval(() => {
                setTimeLeft((prev) => {
                    if (prev <= 1) {
                        clearInterval(warningTimerRef.current);
                        handleLogout();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);

        }, warningTime);
    }, [timeoutDuration, warningDuration, handleLogout]);

    // Activity Handler
    const handleActivity = useCallback(() => {
        // Debounce activity updates slightly
        const now = Date.now();
        if (now - lastActivityRef.current < 1000) return;
        lastActivityRef.current = now;

        // If warning is NOT shown, reset the main timer
        // If warning IS shown, user must click "Stay Logged In" explicitly
        if (!showWarning) {
            startTimer();
        }
    }, [showWarning, startTimer]);

    // Initialize listeners
    useEffect(() => {
        EVENTS.forEach(event => window.addEventListener(event, handleActivity));
        startTimer();

        return () => {
            EVENTS.forEach(event => window.removeEventListener(event, handleActivity));
            if (timerRef.current) clearTimeout(timerRef.current);
            if (warningTimerRef.current) clearInterval(warningTimerRef.current);
        };
    }, [handleActivity, startTimer]);

    // Handle "Stay Logged In"
    const handleStayLoggedIn = async () => {
        setShowWarning(false);
        try {
            // Refresh token and verify session with backend
            await authService.checkAuthStatus();
        } catch (error) {
            console.error("Failed to refresh session", error);
        }
        startTimer();
    };

    return (
        <Dialog
            open={showWarning}
            onOpenChange={() => {}}
            aria-labelledby="session-timeout-title"
            aria-describedby="session-timeout-description"
        >
            <DialogContent
                onEscapeKeyDown={(event) => event.preventDefault()}
                onPointerDownOutside={(event) => event.preventDefault()}
            >
                <DialogHeader>
                    <DialogTitle id="session-timeout-title" className="flex items-center gap-2 text-destructive">
                        <AlertTriangle className="h-5 w-5" />
                        Session Expiring
                    </DialogTitle>
                    <DialogDescription id="session-timeout-description">
                        You have been inactive for a while. For security, you will be automatically logged out in:
                    </DialogDescription>
                </DialogHeader>
                <div className="my-4 flex justify-center">
                    <p className="text-4xl font-bold text-destructive">
                        {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
                    </p>
                </div>
                <DialogFooter>
                    <Button onClick={handleLogout} variant="outline">
                        Log Out Now
                    </Button>
                    <Button onClick={handleStayLoggedIn} autoFocus>
                        Stay Logged In
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default SessionTimeout;
